import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Menu, ChevronDown } from "lucide-react";
import rioplexIcon from "@/assets/rioplex-icon.png";
import { PARTNERS_ENABLED } from "@/lib/featureFlags";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashLink = useCallback((e: React.MouseEvent, href: string) => {
    const [path, hash] = href.split("#");
    if (hash && location.pathname === path) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else if (hash) {
      e.preventDefault();
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const investSubItems = [
    // { name: "Country & Regional Context", href: "/invest#country-regional-context" },
    // { name: "Global Trade & Connectivity", href: "/invest#global-trade-connectivity" },
    // { name: "Key Advantages", href: "/invest#key-advantages" },
    // { name: "Economic Data", href: "/invest#economic-data" },
    { name: "Main Sectors for Investment", href: "/invest/sectors" },
    // { name: "Industrial Real Estate", href: "/invest#industrial-real-estate" },
  ];

  const discoverSubItems = [
    // { name: "Tempor Incididunt", href: "/discover#lorem1" },
    // { name: "Labore Et Dolore", href: "/discover#lorem2" },
    // { name: "Magna Aliqua", href: "/discover#lorem3" },
    // { name: "Ut Enim Ad Minim", href: "/discover#lorem4" },
    { name: "Industrial Parks", href: "/discover#industrial-parks" },
  ];

  const aboutSubItems = [
    { name: "Core Values", href: "/about#core-values" },
    { name: "Mission/Vission", href: "/about#mission-vission" },
    { name: "Board of Directors", href: "/about#board-of-directors" },
    // "Our Partners" shows in local dev but is hidden on the live site
    // (gated by PARTNERS_ENABLED). To launch publicly, set PARTNERS_ENABLED
    // = true in src/lib/featureFlags.ts (also enables the /partners route).
    ...(PARTNERS_ENABLED ? [{ name: "Our Partners", href: "/partners" }] : []),
    // { name: "Ambassadors", href: "/about#ambassadors" },
  ];

  const navLinks = [
    { name: "Home", href: "/", isRoute: true, disabled: false, hasDropdown: false },
    { name: "About Us", href: "/about", isRoute: true, disabled: false, hasDropdown: true, subItems: aboutSubItems },
    { name: "Invest in RioPlex", href: "/invest/sectors", isRoute: true, disabled: false, hasDropdown: true, subItems: investSubItems },
    { name: "Discover RioPlex", href: "/discover", isRoute: true, disabled: false, hasDropdown: true, subItems: discoverSubItems },
    { name: "Become a Member", href: "/membership", isRoute: true, disabled: false, hasDropdown: false },
    { name: "News & Resources", href: "/news", isRoute: true, disabled: false, hasDropdown: false },
    { name: "Contact", href: "/contact", isRoute: true, disabled: false, hasDropdown: false }
  ];

  const toggleMobileDropdown = (name: string) => {
    setOpenMobileDropdown(openMobileDropdown === name ? null : name);
  };

  const handleMouseEnter = (name: string) => {
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const getActiveLink = () => {
    return navLinks.find(link => link.name === activeDropdown) || null;
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background ${isScrolled ? "border-b border-border shadow-lg" : ""}`}
      onMouseLeave={handleMouseLeave}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group mr-8">
            <img 
              src={rioplexIcon} 
              alt="RioPlex" 
              className="w-[45px] h-[46px] transition-transform group-hover:scale-110"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 mx-auto">
            {navLinks.map((link, index) => (
              <React.Fragment key={link.name}>
                {link.disabled ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm font-medium text-foreground cursor-default font-gotham">
                          {link.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Coming Soon</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : link.hasDropdown ? (
                  <div
                    onMouseEnter={() => handleMouseEnter(link.name)}
                    className="relative"
                  >
                    <Link 
                      to={link.href} 
                      className={`text-sm font-medium transition-colors font-gotham flex items-center gap-1 ${activeDropdown === link.name ? 'text-primary' : 'text-foreground hover:text-primary'}`}
                    >
                      {link.name}
                      <ChevronDown 
                        size={14} 
                        className={`transition-transform duration-200 ${activeDropdown === link.name ? 'rotate-180' : ''}`}
                      />
                    </Link>
                  </div>
                ) : (
                  <Link 
                    to={link.href} 
                    className="text-sm font-medium text-foreground transition-colors hover:text-primary font-gotham"
                  >
                    {link.name}
                  </Link>
                )}
                {/* The divider is an empty styled span, so without these text
                    nodes the nav has no character between items and copies /
                    extracts as "Become a MemberNews & ResourcesContact".
                    A whitespace-only anonymous flex item is not rendered, so
                    these add the separation to the text without moving pixels. */}
                {index < navLinks.length - 1 && (
                  <>
                    {" "}
                    <span className="w-[0.64px] h-4 bg-[#64ffa3]" />
                    {" "}
                  </>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="lg:hidden ml-auto">
              <button className="p-2" aria-label="Toggle menu">
                <Menu className="text-foreground" />
              </button>
            </SheetTrigger>
            
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
              <div className="flex flex-col h-full">
                {/* Logo in off-canvas menu */}
                <Link to="/" className="flex items-center mb-8 pt-4">
                  <img 
                    src={rioplexIcon} 
                    alt="RioPlex" 
                    className="w-[45px] h-[46px]"
                  />
                </Link>

                {/* Navigation Links */}
                <nav className="flex flex-col gap-4">
                  {navLinks.map(link => (
                    link.disabled ? (
                      <TooltipProvider key={link.name}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-foreground text-base font-medium cursor-default font-gotham">
                              {link.name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Coming Soon</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : link.hasDropdown && link.subItems ? (
                      <Collapsible 
                        key={link.name}
                        open={openMobileDropdown === link.name}
                        onOpenChange={() => toggleMobileDropdown(link.name)}
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full text-foreground text-base font-medium hover:text-primary transition-colors font-gotham">
                          {link.name}
                          <ChevronDown 
                            size={16} 
                            className={`transition-transform duration-200 ${openMobileDropdown === link.name ? 'rotate-180' : ''}`}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pl-4 mt-2 flex flex-col gap-2">
                          {link.subItems.map((subItem) => (
                            <SheetClose asChild key={subItem.name}>
                              <Link
                                to={subItem.href}
                                className="text-foreground/80 text-sm font-medium hover:text-primary transition-colors font-gotham py-1"
                                onClick={(e) => handleHashLink(e, subItem.href)}
                              >
                                {subItem.name}
                              </Link>
                            </SheetClose>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <SheetClose asChild key={link.name}>
                        <Link 
                          to={link.href} 
                          className="text-foreground text-base font-medium hover:text-primary transition-colors font-gotham"
                        >
                          {link.name}
                        </Link>
                      </SheetClose>
                    )
                  ))}
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Full-width Dropdown Menu */}
      <div
        className={`absolute left-0 right-0 bg-background/70 backdrop-blur-md border-b border-border/50 overflow-hidden transition-all duration-300 ease-out ${
          activeDropdown ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
        }`}
        onMouseEnter={() => activeDropdown && setActiveDropdown(activeDropdown)}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-start justify-center gap-12">
            {getActiveLink() && (
              <Link
                to={getActiveLink()!.href}
                className="text-2xl font-bold text-primary font-gotham shrink-0 pt-1"
                onClick={() => setActiveDropdown(null)}
              >
                {getActiveLink()!.name}
              </Link>
            )}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              {getActiveLink()?.subItems?.map((subItem) => (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors font-gotham py-2 whitespace-nowrap"
                  onClick={(e) => { setActiveDropdown(null); handleHashLink(e, subItem.href); }}
                >
                  {subItem.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
